/**
 * Permission Helper Utility
 * Manages role-based access control for the dashboard
 */

// Role IDs (role_id from the login response)
export const ROLES = {
  SUPERADMIN: "1",
  ADMIN: "2",
  CUSTOMER: "3",
  STORE_MANAGER: "5",
  MARKETING: "6",
  WORKER: "7",
  OPS_UNASSIGNED: "8",
};

// Roles that only get the Operations nav (Store Checklists).
const OPERATIONS_ONLY_ROLES = [ROLES.WORKER, ROLES.OPS_UNASSIGNED];

// Superadmin and admin see the whole admin dashboard.
const isFullAdmin = (role) => role === ROLES.SUPERADMIN || role === ROLES.ADMIN;

/**
 * Get current user role ID from localStorage
 * @returns {string|null} Role ID
 */
export const getUserRole = () => {
  return localStorage.getItem("user_role_id");
};

/**
 * Get current user data from localStorage
 * @returns {object|null} User data object
 */
export const getUserData = () => {
  const userData = localStorage.getItem("user_data");
  return userData ? JSON.parse(userData) : null;
};

/**
 * Check if current user is superadmin (or admin, which has the same access)
 * @returns {boolean}
 */
export const isSuperAdmin = () => {
  return isFullAdmin(getUserRole());
};

/**
 * Check if current user is store manager
 * @returns {boolean}
 */
export const isStoreManager = () => {
  return getUserRole() === ROLES.STORE_MANAGER;
};

/**
 * Check if current user is marketing
 * @returns {boolean}
 */
export const isMarketing = () => {
  return getUserRole() === ROLES.MARKETING;
};

/**
 * Check if user has write/edit permissions
 * Superadmin, Admin and Marketing can create, edit, or delete data
 * @returns {boolean}
 */
export const canWrite = () => {
  const role = getUserRole();
  return isFullAdmin(role) || role === ROLES.MARKETING;
};

/**
 * Check if user can view a specific section
 * @param {string} sectionName - Section identifier
 * @returns {boolean}
 */
export const canViewSection = (sectionName) => {
  const role = getUserRole();

  // Superadmin and Admin can view all sections
  if (isFullAdmin(role)) {
    return true;
  }

  // Workers and unassigned Operations staff: Operations nav only
  if (OPERATIONS_ONLY_ROLES.includes(role)) {
    return sectionName === "store-checklists";
  }

  // Store Manager allowed sections
  if (role === ROLES.STORE_MANAGER) {
    const allowedSections = [
      "products",
      "users",
      "orders",
      "payments",
      "packages",
      "instore-music",
      "shelf-talker",
      "staff",
      "store-checklists",
      "stock-upload",
    ];
    return allowedSections.includes(sectionName);
  }

  // Marketing allowed sections
  if (role === ROLES.MARKETING) {
    const allowedSections = [
      "shelf-talker",
      "promotions",
    ];
    return allowedSections.includes(sectionName);
  }

  // Default: no access
  return false;
};

/**
 * Get allowed sections for current user
 * @returns {array} Array of allowed section names
 */
export const getAllowedSections = () => {
  const role = getUserRole();

  if (isFullAdmin(role)) {
    return [
      "dashboard",
      "products",
      "users",
      "orders",
      "delivery",
      "payments",
      "payment-methods",
      "promotions",
      "staff",
      "packages",
      "settings",
      "reports",
      "instore-music",
      "shelf-talker",
      "template-builder",
      "store-checklists",
      "stock-upload",
    ];
  }

  if (role === ROLES.STORE_MANAGER) {
    return [
      "products",
      "users",
      "orders",
      "payments",
      "packages",
      "instore-music",
      "shelf-talker",
      "staff",
      "store-checklists",
      "stock-upload",
    ];
  }

  if (role === ROLES.MARKETING) {
    return [
      "shelf-talker",
      "promotions",
    ];
  }

  if (OPERATIONS_ONLY_ROLES.includes(role)) {
    return ["store-checklists"];
  }

  return [];
};

/**
 * Check if user can perform specific actions
 * @param {string} action - Action name (create, edit, delete, export, print)
 * @returns {boolean}
 */
export const canPerformAction = (action) => {
  const role = getUserRole();

  // Superadmin and Admin can perform all actions
  if (isFullAdmin(role)) {
    return true;
  }

  // Store Manager permissions
  if (role === ROLES.STORE_MANAGER) {
    // Read-only actions allowed for Store Manager
    const allowedActions = ["view", "search", "filter", "export", "print"];
    return allowedActions.includes(action);
  }

  // Marketing permissions - full access
  if (role === ROLES.MARKETING) {
    return true;
  }

  return false;
};

/**
 * Get role label for display
 * @returns {string} Role label
 */
export const getRoleLabel = () => {
  const role = getUserRole();

  switch (role) {
    case ROLES.SUPERADMIN:
      return "Super Admin Login";
    case ROLES.ADMIN:
      return "Admin Login";
    case ROLES.STORE_MANAGER:
      return "Store Manager Login";
    case ROLES.MARKETING:
      return "Marketing Login";
    case ROLES.WORKER:
      return "Worker Login";
    case ROLES.OPS_UNASSIGNED:
      return "Operations Login";
    default:
      return "Admin Login";
  }
};

/** Roles 7 and 8: only the Operations nav (Store Checklists). */
export const isOperationsOnlyRole = () => OPERATIONS_ONLY_ROLES.includes(getUserRole());

/** Customers (role 3) and unknown roles can't use the dashboard. */
export const canUseDashboard = (roleId = getUserRole()) =>
  Object.values(ROLES).includes(String(roleId)) && String(roleId) !== ROLES.CUSTOMER;

/**
 * Roles tied to one store (store manager, worker, unassigned Operations
 * staff) work on that store only; admins pick a store in the header.
 */
export const hasLockedStore = () =>
  [ROLES.STORE_MANAGER, ...OPERATIONS_ONLY_ROLES].includes(getUserRole());

/** The logged-in user's own store (store_id from the login response), or null. */
export const getUserStoreId = () => getUserData()?.store_id ?? null;

/**
 * Clear user role and data from localStorage (on logout)
 */
export const clearUserRole = () => {
  localStorage.removeItem("user_role_id");
  localStorage.removeItem("user_data");
};
